<template>
  <div class="flex flex-col h-full overflow-y-auto bg-white">
    <!-- Header -->
    <div class="p-4 border-b border-gray-200">
      <h3 class="font-semibold text-gray-900">Detalhes do Lead</h3>
    </div>
    
    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-6">
      <!-- Contact Information -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <UserIcon class="w-4 h-4 mr-2" />
          Informações de Contato
        </h4>
        <div class="space-y-2 text-sm">
          <div>
            <span class="text-gray-500">Nome:</span>
            <p class="font-medium text-gray-900">{{ conversation.contact_name || 'Não informado' }}</p>
          </div>
          <div>
            <span class="text-gray-500">Telefone:</span>
            <p class="font-medium text-gray-900">{{ formatPhone(conversation.contact_phone) }}</p>
          </div>
          <div v-if="conversation.contact_email">
            <span class="text-gray-500">Email:</span>
            <p class="font-medium text-gray-900">{{ conversation.contact_email }}</p>
          </div>
        </div>
      </div>

      <!-- Vehicle Information -->
      <div v-if="conversation.vehicle_model">
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <TruckIcon class="w-4 h-4 mr-2" />
          Informações do Veículo
        </h4>
        <div class="space-y-2 text-sm">
          <div>
            <span class="text-gray-500">Modelo:</span>
            <p class="font-medium text-gray-900">{{ conversation.vehicle_model }}</p>
          </div>
          <div v-if="conversation.vehicle_plate">
            <span class="text-gray-500">Placa:</span>
            <p class="font-medium text-gray-900">{{ conversation.vehicle_plate }}</p>
          </div>
          <div v-if="conversation.vehicle_color">
            <span class="text-gray-500">Cor:</span>
            <p class="font-medium text-gray-900">{{ conversation.vehicle_color }}</p>
          </div>
        </div>
      </div>

      <!-- Tags -->
      <div v-if="conversation.tags && conversation.tags.length > 0">
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <TagIcon class="w-4 h-4 mr-2" />
          Tags
        </h4>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="tag in conversation.tags"
            :key="tag.id"
            class="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium"
          >
            {{ tag.name }}
          </span>
        </div>
      </div>

      <!-- Lead Score -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <ChartBarIcon class="w-4 h-4 mr-2" />
          Score do Lead
        </h4>
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-gray-600">Pontuação</span>
            <span class="font-bold text-primary-600">{{ conversation.lead_score || 0 }}/100</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              :class="[
                'h-2 rounded-full transition-all',
                scoreClass(conversation.lead_score)
              ]"
              :style="{ width: `${conversation.lead_score || 0}%` }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Lead Status -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <FunnelIcon class="w-4 h-4 mr-2" />
          Status no Pipeline
        </h4>
        <select
          v-model="localLeadStatus"
          @change="updateLeadStatus"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="novo">Novo</option>
          <option value="contatado">Contatado</option>
          <option value="qualificado">Qualificado</option>
          <option value="orcamento">Orçamento</option>
          <option value="negociacao">Negociação</option>
          <option value="ganho">Ganho</option>
          <option value="perdido">Perdido</option>
        </select>
      </div>

      <!-- Campaign Origin -->
      <div v-if="conversation.campaign_name">
        <h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <MegaphoneIcon class="w-4 h-4 mr-2" />
          Origem
        </h4>
        <p class="text-sm text-gray-900">{{ conversation.campaign_name }}</p>
        <p v-if="conversation.campaign_date" class="text-xs text-gray-500 mt-1">
          {{ formatDate(conversation.campaign_date) }}
        </p>
      </div>

      <!-- Timestamps -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-3">Informações</h4>
        <div class="space-y-1 text-xs text-gray-500">
          <p v-if="conversation.first_message_at">
            <span class="font-medium">Primeiro contato:</span>
            {{ formatDate(conversation.first_message_at) }}
          </p>
          <p v-if="conversation.last_message_at">
            <span class="font-medium">Última mensagem:</span>
            {{ formatDate(conversation.last_message_at) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import {
  UserIcon,
  TruckIcon,
  TagIcon,
  ChartBarIcon,
  FunnelIcon,
  MegaphoneIcon
} from '@heroicons/vue/24/outline'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useConversationsStore } from '@/stores/conversations'

const props = defineProps({
  conversation: { type: Object, required: true }
})

const conversationsStore = useConversationsStore()
const localLeadStatus = ref(props.conversation?.lead_status || 'novo')

watch(() => props.conversation?.lead_status, (newStatus) => {
  localLeadStatus.value = newStatus || 'novo'
})

function formatPhone(phone) {
  if (!phone) return 'Não informado'
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11) {
    return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`
  }
  return phone
}

function formatDate(timestamp) {
  if (!timestamp) return ''
  try {
    return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  } catch (error) {
    return ''
  }
}

function scoreClass(score) {
  const numScore = score || 0
  if (numScore >= 70) return 'bg-green-500'
  if (numScore >= 40) return 'bg-yellow-500'
  return 'bg-red-500'
}

async function updateLeadStatus() {
  try {
    await conversationsStore.updateStatus(
      props.conversation.id,
      localLeadStatus.value
    )
  } catch (error) {
    console.error('Erro ao atualizar status:', error)
    alert('Erro ao atualizar status do lead')
    // Revert on error
    localLeadStatus.value = props.conversation.lead_status
  }
}
</script>
